import { useSelector } from 'react-redux'
import type { Nil } from '@pbt/pbt-ui-components'

import { PrescriptionStates, PrescriptionType } from '~/constants/prescription'
import i18n from '~/locales/i18n'
import {
  getDrugDeliveryMethod,
  getDrugStrengthColor,
  getDrugStrengthUnit,
  getInventoryProductSizeUnit,
  getPackageType,
  getPrescriptionProductSizeUnit,
  getPrescriptionStates,
  getProductForm,
  getProductSizeUnit,
} from '~/store/reducers/constants'
import type { RxCardItem } from '~/types'
import type {
  Prescription,
  PrescriptionV2,
} from '~/types/entities/prescriptions'
import { useCurrentBusinessTimezone } from '~/utils/useCurrentBusinessTimezone'

import { isOrderedDrugCard } from '../rxCardItem'
import {
  appendShippingColumn,
  computeRelativeTime,
  type DisplayField,
  getInstructionsField,
  getOrderTableFields,
  getOrderTableSubHeader,
  getPrescriptionFlags,
  getPrescriptionStateName,
  getRxFields,
  type OrderTableContent,
  shouldSuppressFields,
} from './rxCardUtilities'
import {
  getShippingRowTexts,
  type RxUsagesWithShipmentsData,
} from './useRxOrderTableShipments'

function applyChewyOrderTableEnhancements(
  orderTableData: OrderTableContent,
  rxUsagesData: RxUsagesWithShipmentsData | Nil,
): OrderTableContent {
  let data = orderTableData
  if (rxUsagesData?.rxUsages?.length) {
    const usages = rxUsagesData.rxUsages
    data = {
      ...data,
      cardHeaders: data.cardHeaders.map((h, i) => ({
        ...h,
        orderNumber:
          usages[i]?.rxOrder?.externalOrderId ?? h.orderNumber ?? undefined,
        rxNo: rxUsagesData.rxNo ?? h.rxNo ?? undefined,
      })),
    }
  }
  const rawShippingTexts = rxUsagesData
    ? getShippingRowTexts(rxUsagesData, true)
    : []
  const rowCount = data.rows.length
  const shippingTexts =
    rawShippingTexts.length >= rowCount
      ? rawShippingTexts.slice(0, rowCount)
      : rawShippingTexts
  return appendShippingColumn(data, shippingTexts, i18n.t('Common:SHIPPING'))
}

interface UseRxContentOptions {
  itemDate?: string
  onDispenseRefill?: () => void
  /** Loaded prescription with partNumber/chewyItems/refills for qty requested (e.g. when order table expanded). */
  prescriptionForQtyAndShipping?: PrescriptionV2 | null
  rxUsagesData?: RxUsagesWithShipmentsData | null
}

export interface RxContent {
  fields: DisplayField[]
  instructions: DisplayField
  isCustomView: boolean
  isVetApproved: boolean
  orderTableData: OrderTableContent | null
  orderTableSubHeader: string | null
  orderTableTitle: string
  prescriptionStateName: string | null
  showTime: string | null
}

const EMPTY_RX_CONTENT: RxContent = {
  fields: [],
  instructions: { label: '', value: '' },
  isCustomView: false,
  isVetApproved: false,
  orderTableData: null,
  orderTableSubHeader: null,
  orderTableTitle: '',
  prescriptionStateName: null,
  showTime: null,
}

/**
 * Hook that fetches Redux data and returns Rx fields and instructions for the card.
 */
export const useRxCardContent = (
  item: RxCardItem | Nil,
  options?: UseRxContentOptions,
): RxContent => {
  const prescription: Prescription | Nil = item?.prescription
  const isOrderedDrug = item ? isOrderedDrugCard(item) : false
  const {
    itemDate,
    onDispenseRefill,
    prescriptionForQtyAndShipping,
    rxUsagesData,
  } = options ?? {}

  const prescriptionStates = useSelector(getPrescriptionStates)
  const prescriptionProductSizeUnit = useSelector(
    getPrescriptionProductSizeUnit,
  )
  const productSizeUnit = useSelector(getProductSizeUnit)
  const inventoryProductSizeUnit = useSelector(getInventoryProductSizeUnit)
  const packageType = useSelector(getPackageType)
  const productForm = useSelector(getProductForm)
  const drugDeliveryMethod = useSelector(getDrugDeliveryMethod)
  const drugStrengthColor = useSelector(getDrugStrengthColor)
  const drugStrengthUnit = useSelector(getDrugStrengthUnit)

  const vetDietConstants =
    inventoryProductSizeUnit && packageType && productForm
      ? {
          InventoryProductSizeUnit: inventoryProductSizeUnit,
          PackageType: packageType,
          ProductForm: productForm,
        }
      : undefined

  const timezone = useCurrentBusinessTimezone()

  if (!prescription) return EMPTY_RX_CONTENT

  const { prescriptionType } = prescription

  const flags = getPrescriptionFlags(
    prescription,
    prescriptionType,
    prescriptionStates,
  )

  const shouldEnableDispenseRefill =
    prescriptionType === PrescriptionType.IN_HOUSE && !flags.isCanceled

  const fields = getRxFields(prescription, prescriptionType, {
    isOrderedDrug,
    prescriptionStates,
    prescriptionProductSizeUnit,
    productSizeUnit,
    vetDietConstants,
    onDispenseRefill: shouldEnableDispenseRefill ? onDispenseRefill : undefined,
  })

  const instructions = getInstructionsField(prescription)
  const prescriptionStateName = getPrescriptionStateName(
    prescription,
    prescriptionStates,
  )
  const isVetApproved = prescriptionStateName === PrescriptionStates.APPROVED

  const isCustomView = shouldSuppressFields(flags)
  const isChewy = prescriptionType === PrescriptionType.CHEWY

  const showTime =
    isCustomView && itemDate ? computeRelativeTime(itemDate, timezone) : null

  const orderTableSubHeader = getOrderTableSubHeader(
    prescription,
    prescriptionType,
  )
  const orderTableTitle: string = i18n.t('Common:ORDERS')

  const baseOrderTableData = isCustomView
    ? null
    : getOrderTableFields(prescription, prescriptionType, {
        isOrderedDrug,
        orderTableLoadedRx: prescriptionForQtyAndShipping ?? undefined,
        prescriptionProductSizeUnit,
        productSizeUnit,
        prescriptionStates,
        vetDietConstants,
        DrugDeliveryMethod: drugDeliveryMethod,
        DrugStrengthColor: drugStrengthColor,
        DrugStrengthUnit: drugStrengthUnit,
        InventoryProductSizeUnit: inventoryProductSizeUnit,
        ProductForm: productForm,
        timezone,
      })

  const orderTableData =
    baseOrderTableData && isChewy
      ? applyChewyOrderTableEnhancements(baseOrderTableData, rxUsagesData)
      : baseOrderTableData

  return {
    fields,
    instructions,
    isCustomView,
    isVetApproved,
    orderTableData,
    orderTableSubHeader,
    orderTableTitle,
    prescriptionStateName,
    showTime,
  }
}
